import { UsageUnitType, Duration } from 'rpc/model/squareup/sub2/data';
import { Currency } from 'rpc/model/squareup/connect/v2/common/money';
import { toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { SoftwareProductGroupType } from 'rpc/model/squareup/solidshop/v2/service/products/list_software_product_groups';

import { ReactComponent as AppointmentsIcon } from 'images/icons/software/appointments-icon.svg';
import { ReactComponent as OnlineIcon } from 'images/icons/software/online-icon.svg';
import { ReactComponent as RestaurantsIcon } from 'images/icons/software/restaurants-icon.svg';
import { ReactComponent as RetailIcon } from 'images/icons/software/retail-icon.svg';
import { ReactComponent as PosIcon } from 'images/icons/software/pos-icon.svg';
import { Money } from 'rpc/model/squareup/common/currency';
import { CurrencyCode } from 'rpc/model/squareup/common/currency_codes';
import { SOFTWARE_BASE_PRODUCT_KEYS } from 'constants/software';

// this should match the subscription states in Solidshop::Software::SoftwareSubscriptionStates
export const SOFTWARE_SUBSCRIPTION_STATES = Object.freeze({
  ACTIVE_SUBSCRIPTION: 'SOFTWARE_ACTIVE_SUBSCRIPTION',
  FREE_TRIAL_EXPIRED: 'SOFTWARE_FREE_TRIAL_EXPIRED',
  FREE_TRIAL_ACTIVE: 'SOFTWARE_FREE_TRIAL_ACTIVE',
  NOT_SUBSCRIBED: 'SOFTWARE_NOT_SUBSCRIBED',
  SUBSCRIPTION_PAUSED: 'SOFTWARE_SUBSCRIPTION_PAUSED',
  SUBSCRIPTION_PRESALE: 'SOFTWARE_SUBSCRIPTION_PRESALE',
  SUBSCRIPTION_WITH_ACTIVE_SUPERSET:
    'SOFTWARE_SUBSCRIPTION_WITH_ACTIVE_SUPERSET',
});

/**
 * Returns the UsageBucket that should drive cost + unit_type rendering for a
 * multi_bucket_pricing plan. Prefers the FNB_KIOSK_DEVICE bucket when present
 * (kiosk P&P plans surface the device fee even when it isn't the first bucket);
 * otherwise falls back to the first bucket.
 * @param {squareup.sub2.data.MultiBucketPricing as JSON} multiBucketPricing
 * @returns {squareup.sub2.data.UsageBucket as JSON | undefined}
 */
const findPrimaryMultiBucket = (multiBucketPricing) => {
  const buckets = multiBucketPricing?.buckets || [];
  return (
    buckets.find((bucket) => bucket?.unit_type === 'FNB_KIOSK_DEVICE') ||
    buckets[0]
  );
};

const isKioskBucket = (bucket) => bucket?.unit_type === 'FNB_KIOSK_DEVICE';

/**
 * Returns the pricing bucket that represents the base (Square Plus) portion of
 * a multi_bucket_pricing kiosk plan. We prefer BILLABLE_LOCATION since that's
 * the bucket SaaS uses for the Plus/Premium base fee.
 * @param {squareup.sub2.data.MultiBucketPricing as JSON} multiBucketPricing
 * @returns {squareup.sub2.data.UsageBucket as JSON | undefined}
 */
const findBaseMultiBucket = (multiBucketPricing) => {
  const buckets = multiBucketPricing?.buckets || [];
  return buckets.find((bucket) => bucket?.unit_type === 'BILLABLE_LOCATION');
};

/**
 * Get the cost (squareup.common.Money) of the Sub2 plan
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @returns {squareup.common.Money as JSON} money
 */
export const getPlanCost = (plan) => {
  const { bucket_pricing, fixed_pricing, multi_bucket_pricing } =
    plan?.pricing_model || {};

  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);
  const kioskBucketCost = isKioskBucket(primaryBucket)
    ? primaryBucket.cost
    : null;

  return (
    bucket_pricing?.cost ||
    fixed_pricing?.cost ||
    kioskBucketCost ||
    multi_bucket_pricing?.baseCost ||
    primaryBucket?.cost ||
    null
  );
};

/**
 * @param {*} plan
 * @returns {CurrencyCode} currency code
 */
export const getPlanCurrency = (plan) => {
  const { bucket_pricing, fixed_pricing, multi_bucket_pricing } =
    plan?.pricing_model || {};

  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);

  return (
    bucket_pricing?.cost?.currency_code ||
    fixed_pricing?.cost?.currency_code ||
    multi_bucket_pricing?.baseCost?.currency_code ||
    primaryBucket?.cost?.currency_code ||
    CurrencyCode.USD
  );
};

/**
 * Break a plan into the individual priced rows we want to surface.
 * For multi_bucket_pricing kiosk plans we return both the base (BILLABLE_LOCATION)
 * and kiosk (FNB_KIOSK_DEVICE) buckets, unless `isPnpSubscribed` is true, in which
 * case the base line is suppressed (merchant already pays the Plus fee elsewhere).
 * For fixed_pricing / bucket_pricing / single-bucket plans we return a single item
 * matching the existing getPlanCost behavior.
 *
 * @param {squareup.solidshop.v2.resources.Plan as JSON} plan
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed] - whether the user already has an
 *   active Square Plus/Premium (P&P) subscription
 * @returns {Array<{ cost: squareup.common.Money as JSON, unitType: string | null, isBase: boolean }>}
 */
export const getSoftwarePlanLineItems = (
  plan,
  { isPnpSubscribed = false } = {},
) => {
  const { multi_bucket_pricing } = plan?.pricing_model || {};

  const baseBucket = findBaseMultiBucket(multi_bucket_pricing);
  const primaryBucket = findPrimaryMultiBucket(multi_bucket_pricing);

  if (
    baseBucket &&
    isKioskBucket(primaryBucket) &&
    baseBucket !== primaryBucket
  ) {
    const lineItems = [
      {
        cost: primaryBucket.cost,
        unitType: primaryBucket.unit_type,
        isBase: false,
      },
    ];

    if (!isPnpSubscribed) {
      lineItems.unshift({
        cost: baseBucket.cost,
        unitType: baseBucket.unit_type,
        isBase: true,
      });
    }

    return lineItems;
  }

  const cost = getPlanCost(plan);
  if (!cost) return [];

  return [
    {
      cost,
      unitType:
        plan?.pricing_model?.bucket_pricing?.unit_type ||
        primaryBucket?.unit_type ||
        null,
      isBase: false,
    },
  ];
};

/**
 * Sum the prices from all software plans, matching the rendered line items.
 * Kiosk P&P plans produce two priced lines (base + device) unless the merchant
 * already has an active Plus/Premium subscription.
 *
 * @param {Array[squareup.solidshop.v2.resources.Plan as JSON]} softwarePlans
 * @param {Object} [options]
 * @param {boolean} [options.isPnpSubscribed]
 * @returns {squareup.common.Money} money
 */
export const sumSoftwarePricing = (
  softwarePlans = [],
  { isPnpSubscribed = false } = {},
) => {
  const summedAmount = softwarePlans.reduce((acc, plan) => {
    const lineItems = getSoftwarePlanLineItems(plan, { isPnpSubscribed });
    const planTotal = lineItems.reduce(
      (sum, item) => sum + (item.cost?.amount || 0),
      0,
    );
    return acc + planTotal;
  }, 0);

  return Money.create({
    amount: summedAmount,
    currencyCode: CurrencyCode[getPlanCurrency(softwarePlans[0])],
  });
};

/**
 * Sort cart summary products by software or hardware
 * @param {Array} products
 * @returns {Hash} sorted products
 */
export const sortProductByHardwareOrSoftware = (products = []) => {
  const sortedProducts = {
    software: [],
    hardware: [],
  };

  products.forEach((product) => {
    if (product.is_software) {
      sortedProducts.software.push(product);
    } else {
      sortedProducts.hardware.push(product);
    }
  });

  return sortedProducts;
};

/**
 * Returns the icon based on the mapped product group type.
 * @param groupType software group type of software
 * @returns svg react icon
 */
export const getSoftwareTypeIcon = (groupType) => {
  const type = SoftwareProductGroupType[groupType];

  switch (type) {
    case SoftwareProductGroupType.APPOINTMENTS: {
      return AppointmentsIcon;
    }
    case SoftwareProductGroupType.ONLINE: {
      return OnlineIcon;
    }
    case SoftwareProductGroupType.RESTAURANTS: {
      return RestaurantsIcon;
    }
    case SoftwareProductGroupType.RETAIL: {
      return RetailIcon;
    }
    case SoftwareProductGroupType.POS: {
      return PosIcon;
    }
    default: {
      return null;
    }
  }
};

/**
 * Returns a localized string for the software price
 * @param plan
 * @returns localized string or null
 */
export const getPlanPrice = (plan) => {
  const moneyModel = getPlanCost(plan);

  return moneyModel
    ? toCurrencyWithMinorUnitAmount(
        moneyModel.amount,
        Currency[moneyModel.currencyCode],
      )
    : null;
};

/**
 * Returns the duration string based on tense.
 * @param t translations t
 * @param durationUnit the duration unit
 * @param isSingular whether the tense is singale or not singular (plural)
 * @returns translation for duration unit
 */
export const getDurationUnitText = (t, durationUnit, isSingular = true) => {
  const baseKey = `cart.software.duration.durationUnits.${durationUnit
    .toString()
    .toLowerCase()}`;

  const type = Duration.DurationUnit[durationUnit];
  switch (type) {
    case Duration.DurationUnit.DAY:
    case Duration.DurationUnit.MONTH:
    case Duration.DurationUnit.YEAR: {
      return t(`${baseKey}.${isSingular ? 'singular' : 'plural'}`);
    }
    default: {
      return null;
    }
  }
};

/**
 * Return a string describing the renewal cycle.
 * @param t translations t
 * @param duration the duration
 * @returns a translated renewal cycle string
 */
export const buildRenewalCycle = (t, duration) => {
  if (!duration.count || !duration.unit) return null;

  const isSingleUnit = duration.count === 1;
  const unit = getDurationUnitText(t, duration.unit, isSingleUnit);

  if (!unit) return null;

  return isSingleUnit
    ? `/${unit}`
    : ` ${t('cart.software.duration.everyXUnits', {
        count: duration.count,
        unit,
      })}`;
};

/**
 * Returns the pricing unit messaging based on pricing unit type.
 * @param t translations t
 * @param usageUnitType UsageUnitType
 * @returns translation for usage unit
 */
export const getPricingUnitTypeText = (t, usageUnitType) => {
  const type = UsageUnitType[usageUnitType];
  switch (type) {
    case UsageUnitType.APPOINTMENTS_LOCATIONS:
    case UsageUnitType.RETAIL_LOCATION:
    case UsageUnitType.RESTAURANTS_LOCATION:
    case UsageUnitType.RESTAURANTS_MOBILE_LOCATION:
    case UsageUnitType.RESTAURANTS_ADDITIONAL_DEVICE:
    case UsageUnitType.FNB_KIOSK_DEVICE: {
      return t(`cart.software.usageUnitTypes.${usageUnitType.toLowerCase()}`);
    }
    default: {
      return null;
    }
  }
};

/**
 *  Returns a string describing the trial period.
 * @param t translations t
 * @param duration the duration
 * @returns a translated strial period string
 */
export const buildTrialDuration = (t, duration) => {
  const count = duration?.count;
  const unit = duration?.unit;

  if (!count || !unit) return null;

  // The text should always be in the singular
  // Ex: 30-day and not 30-days
  const unitString = getDurationUnitText(t, unit, true);

  return t('cart.software.duration.freeTrial', {
    count,
    unit: unitString,
  });
};

export const buildPricingLabel = (t, plan) => {
  // The Sub2 Pricing proto uses oneof multiple product pricing strctures, so we must check which
  // is populated, then build the pricing string.
  // https://prototype.sqprod.co/#/docs/squareup.solidshop.v2.resources.Plan#pricing_model
  // Ex: 30-day free trial, then $89/month per location

  if (!plan) return null;
  if (!plan?.renewal_cycle) return null;

  let pricing = '';
  const renewalUnit = buildRenewalCycle(t, plan.renewal_cycle);

  if (!renewalUnit) return null;

  const price = getPlanPrice(plan);

  if (plan.pricing_model?.bucket_pricing) {
    if (!plan.pricing_model?.bucket_pricing.unit_type) return null;

    if (!price) return null;

    const unitText = getPricingUnitTypeText(
      t,
      plan.pricing_model?.bucket_pricing.unit_type,
    );

    if (!unitText) return null;

    pricing = t('cart.software.label.pricing', {
      price,
      renewalUnit,
      unitText,
    });
  } else if (plan.pricing_model?.fixed_pricing) {
    if (!price) return null;

    pricing = `${price}${renewalUnit}`;
  } else if (plan.pricing_model?.multi_bucket_pricing) {
    const primaryBucket = findPrimaryMultiBucket(
      plan.pricing_model?.multi_bucket_pricing,
    );
    if (!primaryBucket?.unit_type) return null;

    if (!price) return null;

    const unitText = getPricingUnitTypeText(t, primaryBucket.unit_type);
    if (!unitText) return null;

    pricing = t('cart.software.label.pricing', {
      price,
      renewalUnit,
      unitText,
    });
  } else {
    // No pricing model exist, so use label
    // pricing_model.tieredPricing not implemented
    pricing = plan.pricing_model?.label || '';
  }

  const trialMessage = plan.free_trial_duration
    ? buildTrialDuration(t, plan.free_trial_duration)
    : null;

  if (!trialMessage) return pricing;

  return t('cart.software.label.pricingOptions', {
    trialMessage,
    pricing,
  });
};

/**
 * Returns the price per renewal cycle for a software product.
 *
 * For multi-line kiosk plans (P&P Plus/Premium with both BILLABLE_LOCATION
 * and FNB_KIOSK_DEVICE buckets, surfaced when `saas-shop-kiosk-improvements`
 * is on) this sums every visible line item so the cart row matches the
 * Subscriptions subtotal.
 *
 * For everything else — legacy `fnb-kiosk`, fixed_pricing, bucket_pricing,
 * single-bucket multi_bucket, all non-kiosk software — we delegate to the
 * pre-existing `getPlanPrice` path so behavior with the flag off is byte-for-
 * byte unchanged.
 *
 * @param {Object} options
 * @param {CartSummaryPlan} options.plan - software plan
 * @param {TFunction} options.t - i18n t function
 * @param {boolean} [options.isPnpSubscribed] - whether the user already has an
 *   active Square Plus / Premium subscription (suppresses the base line)
 * @returns {String} softwarePrice - price per renewal cycle in the form of $69/month
 */
export const getSoftwarePriceLabel = ({ plan, t, isPnpSubscribed = false }) => {
  if (!plan) return null;

  const renewalCycle = buildRenewalCycle(t, plan.renewal_cycle);
  if (!renewalCycle) return null;

  const lineItems = getSoftwarePlanLineItems(plan, { isPnpSubscribed });

  // Single-line plans preserve the pre-PR rendering exactly so non-kiosk and
  // FF-off paths are unchanged.
  if (lineItems.length <= 1) {
    const planPriceLabel = getPlanPrice(plan);
    return planPriceLabel ? `${planPriceLabel}${renewalCycle}` : null;
  }

  const totalAmount = lineItems.reduce(
    (sum, item) => sum + (item.cost?.amount || 0),
    0,
  );
  const currencyCode = lineItems[0].cost?.currency_code;

  if (totalAmount === 0 || !currencyCode) return null;

  // `currencyCode` here is the proto field value (e.g., "USD"); pass straight
  // to Dinero — wrapping in `Currency[...]` flips a string to its numeric
  // value, which Dinero rejects with "Invalid currency code".
  const planPriceLabel = toCurrencyWithMinorUnitAmount(
    totalAmount,
    currencyCode,
  );
  return planPriceLabel ? `${planPriceLabel}${renewalCycle}` : null;
};

/**
 * Checks if the cart contains software with free hardware
 * used to block checkout if true
 * @param {Object} orderData - order data from solidshop
 * @returns {Boolean} isSoftwareWithFreeHardware
 */
export const isSoftwareWithFreeHardware = (orderData) => {
  if (!orderData) return false;

  const { software } = sortProductByHardwareOrSoftware(orderData.cart_summary);

  return Number(orderData.total.amount) === 0 && software.length > 0;
};

/**
 * Fetches the summary for a single software product
 * @param {Array} orderData - order data from solidshop
 * @param {Object} softwareProduct - software product
 * @returns {Object} summary - software summary parsed from order data and software summary
 */
export const fetchSoftwareSummary = (orderData, softwareProduct) => {
  if (!orderData) return null;

  const { software_summary: softwareSummary } = orderData;
  const summary = softwareSummary[softwareProduct.product_key];
  if (!summary) return null;

  return {
    baseProductKey: softwareProduct.base_product_key,
    softwareName: softwareProduct.product_variant.title,
    subscriptionState: summary.subscription_state,
  };
};

/**
 * Fetches the subscription states for the software products in the cart
 * @param {Array} orderData - order data from solidshop
 * @returns {Array} softwareSummaries - array of software subscription states
 */
export const fetchSoftwareSummaries = (orderData) => {
  if (!orderData) return [];

  const { cart_summary: cartSummary } = orderData;
  const { software } = sortProductByHardwareOrSoftware(cartSummary);

  return software
    .map((product) => {
      return fetchSoftwareSummary(orderData, product);
    })
    .filter(Boolean);
};

/**
 * Checks if any software in the cart has subscription states that would block checkout
 * @param {Array} orderData - order data from solidshop
 * @returns {Boolean} anySoftwareBlockingCheckout
 */
export const anySoftwareBlockingCheckout = (orderData) => {
  if (!orderData) return false;

  const softwareSummaries = fetchSoftwareSummaries(orderData);
  if (softwareSummaries.length === 0) return false;

  const blockingStates = new Set([
    SOFTWARE_SUBSCRIPTION_STATES.ACTIVE_SUBSCRIPTION,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_PAUSED,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_PRESALE,
    SOFTWARE_SUBSCRIPTION_STATES.SUBSCRIPTION_WITH_ACTIVE_SUPERSET,
  ]);

  return softwareSummaries.some((summary) => {
    return blockingStates.has(summary.subscriptionState);
  });
};

/**
 * when redirected from a login, check if any software require displaying messaging to the user
 * only allow redirect if all software in cart has a state of not subscribed
 * @param {Array} orderData - order data from solidshop
 * @returns {Boolean} anySoftwareBlockingCheckoutLoginRedirect
 */
export const anySoftwareBlockingCheckoutLoginRedirect = (orderData) => {
  if (!orderData) return false;

  const softwareSummaries = fetchSoftwareSummaries(orderData);
  if (softwareSummaries.length === 0) return false;

  return softwareSummaries.some(
    (summary) =>
      summary.subscriptionState !== SOFTWARE_SUBSCRIPTION_STATES.NOT_SUBSCRIBED,
  );
};

export const fetchSubscriptionState = (product) => {
  return product.software_summary?.subscription_state || null;
};

/**
 * Fetches the trial end date for a software product
 * @param {Object} product - software product
 * @returns {String} trialEndDate - object that looks like
 * { year: 2025, month_of_year: 1, day_of_month: 1 }
 */
export const fetchTrialEndDate = (product) => {
  return product.software_summary?.free_trial_end_date || null;
};

export const isKioskOnlySoftwareInCart = (orderData) => {
  if (!orderData) return false;

  const { software } = sortProductByHardwareOrSoftware(orderData.cart_summary);

  const kioskIsInCart = software.some(
    (product) =>
      product.base_product_key === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK,
  );

  return kioskIsInCart && software.length === 1;
};

export const pullSoftwareFromAddOns = (addOns) => {
  const software = Object.entries(addOns).reduce((acc, [key, value]) => {
    if (key.startsWith('software_')) {
      acc.push(value);
    }

    return acc;
  }, []);

  return software.flat();
};
